import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import planoService from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import WorkspaceOverview, { type WorkspaceMetric } from './dashboard/WorkspaceOverview';
import Button from './ui/Button';
import { Table, Tbody, Td, Th, Thead, Tr } from './ui/Table';
import CollapseToggle from './dashboard/CollapseToggle';

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('pt-BR').format(date);
};

const GestorJuridicoWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [todosPlanos, setTodosPlanos] = useState<PlanoTrabalho[]>([]);
  const [collapsedBoxes, setCollapsedBoxes] = useState<Set<string>>(() => new Set(['acoes-rapidas']));

  const toggleBox = (id: string) => setCollapsedBoxes(prev => {
    const next = new Set(prev);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    return next;
  });
  const isBoxCollapsed = (id: string) => collapsedBoxes.has(id);
  const renderCollapseButton = (id: string) => (
    <CollapseToggle collapsed={isBoxCollapsed(id)} onToggle={() => toggleBox(id)} />
  );
  const fixedCardClass = 'flex h-fit min-h-0 flex-col overflow-hidden rounded-lg border shadow-sm';
  const fixedCardBodyClass = 'min-h-0 flex-1 overflow-y-auto';

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        // minhaFila=false: todos na PGM para ter todos os números
        const data = await planoService.listAll({ minhaFila: false }).catch(() => []);
        if (!active) return;
        setTodosPlanos(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!active) return;
        setError(err?.response?.data?.error || 'Não foi possível carregar a fila jurídica.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const stats = useMemo(() => {
    const semProcurador = todosPlanos.filter(p => !p.procuradorJuridicoId).length;
    const comProcurador = todosPlanos.filter(p => Boolean(p.procuradorJuridicoId)).length;
    return { total: todosPlanos.length, semProcurador, comProcurador };
  }, [todosPlanos]);

  const planosParaDistribuir = useMemo(
    () => todosPlanos.filter(p => !p.procuradorJuridicoId).slice(0, 6),
    [todosPlanos]
  );

  const cards: WorkspaceMetric[] = [
    { label: 'Total na etapa PGM', value: stats.total, tone: 'neutral' },
    { label: 'A distribuir', value: stats.semProcurador, tone: 'warning' },
    { label: 'Com procurador vinculado', value: stats.comProcurador, tone: 'progress' },
  ];

  return (
    <div className="w-full space-y-6">
      <WorkspaceOverview
        profile="Gestor Jurídico"
        description="Distribuição da fila jurídica e acompanhamento dos procuradores."
        metrics={cards}
        loading={loading}
      />

      {/* A fila é o trabalho principal: ocupa 2/3 da largura; os atalhos ficam ao lado. */}
      <div className="grid items-start gap-6 xl:grid-cols-3">
        <div className={`${fixedCardClass} border-line bg-surface xl:col-span-2`}>
          <div className="flex items-center justify-between border-b border-line bg-surface-muted px-5 py-4">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-fg-muted">
                Planos aguardando distribuição
              </h2>
              <p className="mt-1 text-sm text-fg-muted">Sem procurador vinculado.</p>
            </div>
            <div className="ml-3 flex shrink-0 items-center gap-2">
              <Button variant="secondary" onClick={() => navigate('/dashboard/gestor-juridico')}>
                Abrir fila completa
              </Button>
              {renderCollapseButton('planos-distribuicao')}
            </div>
          </div>

          {!isBoxCollapsed('planos-distribuicao') && (loading ? (
            <div className="px-5 py-10 text-center text-sm text-fg-muted">Carregando...</div>
          ) : error ? (
            <div className="px-5 py-10 text-center text-sm text-danger-text">{error}</div>
          ) : planosParaDistribuir.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-fg-muted">
              {stats.total === 0
                ? 'Nenhum plano na etapa PGM no momento.'
                : 'Todos os planos já possuem procurador vinculado.'}
            </div>
          ) : (
            <div className={`${fixedCardBodyClass} overflow-x-auto`}>
              <Table>
                <Thead>
                  <Tr className="border-b border-line">
                    <Th className="text-fg">Plano</Th>
                    <Th className="text-fg">Protocolo</Th>
                    <Th className="text-fg">Atualizado</Th>
                    <Th align="right" className="text-fg">Ação</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {planosParaDistribuir.map(item => (
                    <Tr key={item.id} className="border-b border-slate-100 last:border-b-0">
                      <Td>
                        <div className="font-semibold text-fg">{item.titulo || 'Não informado'}</div>
                        {item.emendaCodigo && (
                          <div className="text-xs text-fg-muted">Emenda {item.emendaCodigo}</div>
                        )}
                      </Td>
                      <Td className="text-fg-muted">{item.numeroProtocolo || '-'}</Td>
                      <Td className="text-fg-muted">
                        {formatDate(item.dataAtualizacao || item.enviadoEm)}
                      </Td>
                      <Td align="right">
                        <Button variant="info" size="sm" onClick={() => navigate(`/dashboard/plano/full/${item.id}`)}>
                          Vincular procurador
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </div>
          ))}
        </div>

        <div className={`${fixedCardClass} border-line bg-surface`}>
          <div className="flex shrink-0 items-center justify-between gap-3 px-5 py-3">
            <h2 className="text-lg font-bold text-fg">Ações rápidas</h2>
            {renderCollapseButton('acoes-rapidas')}
          </div>
          {!isBoxCollapsed('acoes-rapidas') && (
          <div className={`${fixedCardBodyClass} space-y-3 px-5 pb-4`}>
            <Button variant="secondary" className="w-full" onClick={() => navigate('/dashboard/gestor-juridico')}>
              <p className="text-sm font-semibold text-fg">Fila jurídica completa</p>
              <p className="mt-1 text-sm leading-6 text-fg-muted">
                Gerenciar a distribuição de todos os planos na etapa PGM.
              </p>
            </Button>
            <Button variant="secondary" className="w-full" onClick={() => navigate('/dashboard/gestor-juridico?aba=todos')}>
              <p className="text-sm font-semibold text-fg">Ver todos os planos na PGM</p>
              <p className="mt-1 text-sm leading-6 text-fg-muted">
                Visualizar todos os planos, incluindo os já atribuídos.
              </p>
            </Button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GestorJuridicoWorkspace;
